import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useOutsideClick} from 'tea-component';
import {isEmpty} from 'lodash-es';
import {useMemoizedFn, useRequest} from 'ahooks';
import {useIntl, useParams,shallowEqual, useSelector} from 'umi';
import classNames from "classnames";
import { calculateDashboardChart, previewDashboardChartSql } from '@dbl/services/dashboard';
import type { IDashboardChartRequest } from '@dbl/services/dashboard';
import {
  convertToString,
  formatStatisticsData,
  useDblIntl
} from '@/pages/datalab/utils/utils';
import {SYSTEM_NAME} from '@dbl/core/global';
import {dashboardPageBtnPoints} from "@/pages/datalab/utils/buiredDatalabBtnPoints/dashboardPageBtnPoint";
import { ChartType, ConfigChartType } from '@dbl/core/chart';
import Abnormal from '@home/components/Abnormal';
import DragCardHeader from './DragCardHeader';
import useFullScreen from '@home/components/FullScreenScale/_hooks/useFullScreen';
import TableAnalytics from "@customComp/TableAnalytics";
import { usePreviewSQL, PreviewSQLModal } from '@dbl/domainComponents/dataSource';
import {useTableCardPaginationLayout} from './useTableCardPaginationLayout';
import './index.scss';
import { getOldChartDisplayConfig } from '@dbl/core/chartEditor';
import { ITableAnalyticsData } from '@customComp/AliTableAnalytics/types';
import { useChartCardError } from '@dbl/components/DashboardLayout/components/DragShell/hooks/useChartCardError';
import { ChartCalculateMode } from '@dbl/core/chartEditor';

const TableTragCard = (props: any) => {
  const {
    deleteCard,
    handleOnSubmit,
    dataMsg,
    filterValues,
    userFilter,
    hasChartPrivilege,
    system = 'datalab',
    isEdit,
    chartExplore,
    // onForceClose,
    menuId,
    chartsInfoLoading,
    chartSwitchNode,
    isActive = true,
    shouldRequest = true,
    switchRequestId = 0,
    cacheKey,
    onTypeDataLoaded,
    onCompareTimeFiltersChange,
    onDownloadParamsChange,
  } = props;
  const currentGame = useSelector((state: any) => state?.datalab?.currentGame, shallowEqual);
  const {formatMessage} = useIntl();
  const {task_id: taskId, mode } = dataMsg || {};
  const tableRef = useRef();
  const downloadParamsRef = useRef<IDashboardChartRequest | Record<string, never>>({});
  // 对比时间
  const [compareTimeData, setCompareTimeData] = useState({});
  
  // 表格全屏功能
  const fullScreenContent = useRef<HTMLDivElement>(null);
  const { isFullScreen, requestFullScreen, exitFullScreen } = useFullScreen(fullScreenContent);
  
  // 经分承接页gameCode
  const {gamecode}: any = useParams();

  const buildChartParams = useMemoizedFn((queries?: Record<string, unknown>): IDashboardChartRequest => ({
    game_code: gamecode,
    task_id: [String(taskId)],
    queries: {
      filters: filterValues,
      user_filter: userFilter,
      ...queries,
    },
    system,
    cache_key: cacheKey,
    is_sql_preview: false,
  }));

  const resolvePreviewSqlParams = useMemoizedFn((): IDashboardChartRequest => {
    if (!isEmpty(downloadParamsRef.current)) {
      return downloadParamsRef.current as IDashboardChartRequest;
    }
    return buildChartParams();
  });

  const {
    previewSQL,
    loading: previewSqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql: () =>
      previewDashboardChartSql(ChartType.Table, resolvePreviewSqlParams()),
  });

  const { isEn} = useDblIntl('create_dashboard');
  const requestData = useMemoizedFn((queries?: Record<string, unknown>) => {
    const params = buildChartParams(queries);
    downloadParamsRef.current = params;
    onDownloadParamsChange?.(downloadParamsRef.current);
    return calculateDashboardChart(ChartType.Table, params).then((data) => {
      if (!isEmpty(data?.[dataMsg?.task_id])) {
        const {
          table_data: tableData,
          column_name: columnName,
          crosstab_columns: crosstabColumns,
          count,
          actual_count: actualCount,
          // [legacy · 重构可删] 同 Analysis/model getDataAnlysis；统计行见 table_data.is_statistics
          extra_table_data: statisticsData,
          chart_detail,
          user_filter,
          filters
        } = data[dataMsg?.task_id];
        
        const config = JSON.parse(chart_detail?.column_infos || '{}');
        const rightConfig = config?.chartsCfgSave ?? {};
        const {
          field_setting: fieldSetting,
          description,
          conditional_format: conditionalFormat,
          freeze,
          merge_same_cells: mergeSameCells,
        } =
          rightConfig[ChartType.Table]?.customizeSetting?.[
            ConfigChartType[ChartType.Table]
          ] ?? {};
        const tableConfig = JSON.parse(dataMsg?.column_infos || '{}');
        // 处理表格数据
        const newTableData = (tableData || [])?.map((item: ITableAnalyticsData) => 
          Object.keys(item).reduce((acc: ITableAnalyticsData, key) => {
            const value = item?.[key]?.value;
            // 数据兜底兼容
            acc[key] = {
              ...item[key],
              value: convertToString(value)
            };
            return acc;
          }, {}));
        
        const dateFilters = filters?.filter(item => item.column_type === 'date') || [];
        // 和后端确认过, is_vs_compare_time 为true时, 代表是vs图标中的base time 筛选器字段
        // const compareFilter = dateFilters.find(item => !isEmpty(item?.compare_time));
        const compareFilter = dateFilters?.find(item => item?.is_vs_compare_time);
        const baseTimeFilter = compareFilter || dateFilters[0];
        
        if (baseTimeFilter) {
          setCompareTimeData({
            filterId: baseTimeFilter?.filterKey || baseTimeFilter?.table_id,
            defaultCompareTimes: Array.isArray(baseTimeFilter?.compare_time) ? baseTimeFilter?.compare_time : [],
            baseTimeRange: Array.isArray(baseTimeFilter?.value) ? baseTimeFilter?.value : [],
            baseTimeName: baseTimeFilter?.filter_name || baseTimeFilter?.display_name || baseTimeFilter?.column,
            timeGranularity: baseTimeFilter?.dateType,
            chartFilter: baseTimeFilter,
          });
        } else {
          // 说明不存在 日期筛选器控制图表, 此时不显示vs图标
          setCompareTimeData({});
        }
        
        return {
          table: {
            columnName: columnName?.map(column => {
              const { accuracy, unit } = getOldChartDisplayConfig({...column, column_type: column.column_date_type}, ChartType.Table);
              return {...column, accuracy, unit};
            }) || [],
            crosstabColumns,
            data: newTableData,
            actualCount,
            count,
            // [legacy · 重构可删] extra_table_data 已废弃，恒为 [] 时无效果
            statisticsData: [...(formatStatisticsData(statisticsData) || [])]
          },
          chartConfig: {
            description: description && Object.keys(description).reduce((acc, key) => {
              acc[key] = description?.[key]?.desc;
              return acc;
            }, {}),
            fieldSetting,
            conditionalFormat,
            freeze,
            mergeSameCells,
            pageSize: tableConfig?.savePaginAtion || 50,
            // 旧图表保存时, 后端返回的字段名称有空格
            // 现在后端返回的字段名称缺少了之前数据的空格.
            // 导致 进行比对的时候, 无法相等, 排序失效
            // 所以这里将保存好的sorts中的字段名称去掉空格
            sorts: tableConfig?.sorts?.map(item => ({...item, code: item?.code?.trim()})),
            tableColumnWidths: JSON.parse(tableConfig?.saveWidth || '[]')
          },
          filters,
          userFilterInfo: user_filter,
        };
      }
      return {};
    });
  });
  // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求
  const {data, loading, error, run: requestTableData}: any = useRequest(requestData, {
    manual: true,
    onError: (e) => {
      console.error('报表表格请求错误: ', e);
    },
    onSuccess: (data) => {
      downloadParamsRef.current.sorts = data?.chartConfig?.sorts || [];
      onDownloadParamsChange?.(downloadParamsRef.current);
      onTypeDataLoaded?.(ChartType.Table);
    }
  });
  
  // 扩展支持图表类型切换时, 使用该卡片时, 需要传入 isActive, shouldRequest, switchRequestId, cacheKey, onTypeDataLoaded 参数
  // isActive: 是否激活, 默认激活
  // shouldRequest: 是否需要请求数据, 默认需要
  // switchRequestId: 切换请求id, 默认0
  // cacheKey: 缓存key
  // onTypeDataLoaded: 类型数据加载完成回调
  useEffect(() => {
    if (!isActive) return;
    if (!shouldRequest) return;
    if (chartsInfoLoading) return;
    if (!filterValues && !userFilter?.controls?.length) return;
    requestTableData();
  }, [filterValues, chartsInfoLoading, userFilter, isActive, shouldRequest, switchRequestId]);
  
  
  const title = isEn ? dataMsg?.name : (dataMsg?.name_cn || dataMsg?.name);

  // 判断表格数据是否超出行数限制（与 AnalysisLineChart 中 isOverLimit 逻辑保持一致）
  // rowLimit 从 getChartList 返回的 dataMsg.column_infos.saveRowLimits 中获取
  // saveRowLimits 的 key 为图表名称
  // actualCount: 后端实际返回的数据行数
  const isOverLimit = useMemo(() => {
    try {
      const actualCount = data?.table?.actualCount;
      if (typeof actualCount !== 'number') return false;
      const tableConfig = JSON.parse(dataMsg?.column_infos || '{}');
      const saveRowLimits = tableConfig?.saveRowLimits;
      if (!saveRowLimits || typeof saveRowLimits !== 'object') return false;
      const rowLimit = saveRowLimits?.[dataMsg?.name];
      if (typeof rowLimit !== 'number' || rowLimit <= 0) return false;
      return actualCount >= rowLimit;
    } catch (error) {
      console.error('判断表格数据是否超出行数限制错误: ', error);
      return false;
    }

  }, [data?.table?.actualCount, dataMsg?.column_infos, dataMsg?.task_id, dataMsg?.name, dataMsg?.name_cn]);
  
  const jumpToEdit = () => {
    // 重置离开弹窗操作在最外层index中
    // dispatch({
    //   type: 'customDashboard/save',
    //   payload: {
    //     isHideCustomLeaveModal: true,
    //   }
    // })
    const pathId = dataMsg.type === 1 ? 'Action/ExploreAnalysis' : 'Analysis/MultiAnalysis';
    const path = `/${SYSTEM_NAME}/${gamecode}/${pathId}?from=edit&taskId=${dataMsg.task_id}&name=${dataMsg?.name}&nameCn=${dataMsg?.name_cn}`;
    
    handleOnSubmit?.(path);
  };

  const [isActived, setIsActived] = useState(false);
  const [style, setStyle] = useState({});
  const fullBubbleRef = useRef(null);
  const { listen } = useOutsideClick(fullBubbleRef);
  listen(() => {
    setIsActived(false);
  });

  useEffect(() => {
    if (!isFullScreen) return;
    const fullDom = document.querySelector(`.dbl-fullscreen-${taskId}`);
    const pageDom = fullDom?.querySelector('.tea-pagination .tea-pagination__operate .tea-dropdown');
    const handlePageClick = (e: any) => {
      const rect = pageDom?.getBoundingClientRect();
      if (rect?.x && rect?.y) {
        setStyle({ position: 'fixed', left: `${rect?.x}px`, top: `${rect?.y - 180}px` });
        setIsActived(true);
      }
      isFullScreen && e.stopPropagation(); // 阻止事件冒泡
    };
    pageDom?.addEventListener('click', handlePageClick);
    return () => {
      pageDom?.removeEventListener('click', handlePageClick);
    };
  }, [isFullScreen, taskId]);
  
  const failedMsg = useChartCardError(error);

  const paginationLayoutAdaptEnabled =
    !isFullScreen &&
    !(loading || chartsInfoLoading) &&
    !error &&
    Boolean(data?.table?.data?.length);
  const dashboardPaginationLayout = useTableCardPaginationLayout(
    fullScreenContent,
    paginationLayoutAdaptEnabled,
  );

  return (<div
    className={classNames(`dbl-fullscreen dbl-fullscreen-${taskId} dbl-card-drag dbl-card-drag-table`, {'dbl-fullscreen-context': isFullScreen})}
    ref={fullScreenContent}>
    <DragCardHeader
      isView={!isEdit}
      showVs={mode === ChartCalculateMode.Aggregate}
      title={title}
      isOverLimit={isOverLimit}
      dataMsg={dataMsg}
      loading={loading || chartsInfoLoading}
      error={error}
      downloadParams={downloadParamsRef.current}
      filters={data?.filters}
      userFilterInfo={data?.userFilterInfo}
      filterValues={filterValues}
      hasChartPrivilege={hasChartPrivilege}
      jumpToEdit={jumpToEdit}
      deleteCard={deleteCard}
      showDownloadLimit={true}
      system={system}
      chartExplore={chartExplore}
      // onForceClose={onForceClose}
      menuId={menuId}
      fullScreenContent={fullScreenContent}
      isFullScreen={isFullScreen ? true : (loading || error || !data?.table?.data?.length) ? undefined : false}
      openFullscreen={(type: boolean) => {
        if (type) {
          requestFullScreen();
        } else {
          exitFullScreen();
        }
      }}
      tableSortProps={{
        onReset: () => {
          tableRef?.current?.changeSorts(data?.chartConfig?.sorts);
          dashboardPageBtnPoints({
            buttonId: '001',
            buttonName: 'Reset Sort',
            uid: currentGame?.game_code,
            gameName: currentGame?.game_name,
            extInfo: {
              chartId: String(taskId),
              chartName: title,
            }
          });
        },
        onClear: () => {
          tableRef?.current?.changeSorts([]);
          dashboardPageBtnPoints({
            buttonId: '002',
            buttonName: 'Clear Sort',
            uid: currentGame?.game_code,
            gameName: currentGame?.game_name,
            extInfo: {
              chartId: String(taskId),
              chartName: title,
            }
          });
        }
      }}
      compareTimeData={compareTimeData}
      requestTableData={requestTableData}
      chartSwitchNode={chartSwitchNode}
      onCompareTimeFiltersChange={onCompareTimeFiltersChange}
      previewSQL={previewSQL}
    />
      
    <Abnormal
      style={{
        width: '100%',
        height: '100%'
      }}
      isLoading={loading || chartsInfoLoading}
      isError={error}
      nodataMsg={formatMessage({id: "dbl_common_no_filter_data"})}
      failedMsg={failedMsg}
      isNoData={(loading || error) ? false : !data?.table?.data?.length}
      component={<TableAnalytics
        ref={tableRef}
        className={classNames('dbl-dashboard-table', {'dbl-table-small-row-height': isFullScreen})}
        isEn={isEn}
        table={data?.table}
        chartConfig={data?.chartConfig}
        dashboardPaginationLayout={dashboardPaginationLayout}
        sortable={!isEdit}
        isColumnResize={false}
        headerBubbleConfig={fullScreenContent?.current ? {
          popupContainer: fullScreenContent.current,
          positionFixed: false,
        } : undefined}
        saveTableState={(state) => {
          // 将表格的状态存储在ref中, 用于下载时上传参数
          const { sorts } = state || {};
          if (sorts) {
            downloadParamsRef.current.sorts = sorts || [];
            onDownloadParamsChange?.(downloadParamsRef.current);
          }
        }}
      />}
    />
    {isFullScreen && isActived && <div ref={fullBubbleRef} className="tea-overlay" data-placement="top" style={style}>
		  <div className="tea-dropdown-box size-auto-width" style={{position: "relative"}}>
			  <div style={{
          position: "relative",
          height: "165px",
          width: "100%",
          overflow: "auto",
          willChange: "transform",
          direction: "ltr"
        }}>
				  <ul className="tea-list tea-list--option"
				      style={{maxHeight: "none", height: "252px", width: "100%", overflowY: "hidden"}}>
            {
              [10, 20, 30, 40, 50, 100, 200]?.map((item, index) => (<li
                key={item}
                className={classNames({ 'is-selected': tableRef.current?.getPageSize() === item })}
                onClick={() => {
                  tableRef.current?.changePageSize(item);
                  setIsActived(false);
                }}
                style={{
                  position: "absolute",
                  left: "0px",
                  top: `${index * 36}px`,
                  height: "36px",
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                {item}
              </li>))
            }
				  </ul>
			  </div>
		  </div>
	  </div>}
    <PreviewSQLModal
      visible={previewSqlModalVisible}
      onClose={hidePreviewSqlModal}
      sql={previewSqlContent}
      loading={previewSqlLoading}
      error={previewSqlError}
    />
  </div>
  );
};

export default TableTragCard;
